import { DateTime } from 'luxon'
import Locker from '../locker/Locker'
import LockerType from '../locker/LockerType'
import DurationPeriod from '../period/DurationPeriod'
import Unit from '../unit/Unit'
import { RentalStatus } from './enums/RentalStatus'
import Authentication from '../authentication/Authentication'
import RentalJson from './interfaces/RentalJson'
import RentalConfiguration from './RentalConfiguration'

export default class Rental {
  id: string
  status: RentalStatus
  unit: Unit
  locker: Locker
  lockerType: LockerType
  authentications: Array<Authentication>
  durationPeriod: DurationPeriod
  startedDate: DateTime
  endDateIncludingOvertimeGracePeriod: DateTime
  endedDate: DateTime | null
  rentalEndingScheduledAt: DateTime | null
  configuration: RentalConfiguration
  lockerOpenedHistory: Array<DateTime>
  isLockerTransfered: boolean
  lockerTransferedAt: DateTime | null
  lockerTransferedReason: string | null

  static readonly MINUTES_TO_SHOW_WARNING_BEFORE_END: number = 15

  constructor(
    id: string,
    status: RentalStatus,
    unit: Unit,
    locker: Locker,
    lockerType: LockerType,
    authentications: Array<Authentication>,
    durationPeriod: DurationPeriod,
    startedDate: DateTime,
    endDateIncludingOvertimeGracePeriod: DateTime,
    endedDate: DateTime | null,
    rentalEndingScheduledAt: DateTime | null,
    configuration: RentalConfiguration,
    lockerOpenedHistory: Array<DateTime>,
    isLockerTransfered: boolean,
    lockerTransferedAt: DateTime | null,
    lockerTransferedReason: string | null,
  ) {
    this.id = id
    this.status = status
    this.unit = unit
    this.locker = locker
    this.lockerType = lockerType
    this.authentications = authentications
    this.durationPeriod = durationPeriod
    this.startedDate = startedDate
    this.endDateIncludingOvertimeGracePeriod =
      endDateIncludingOvertimeGracePeriod
    this.endedDate = endedDate
    this.rentalEndingScheduledAt = rentalEndingScheduledAt
    this.configuration = configuration
    this.lockerOpenedHistory = lockerOpenedHistory
    this.isLockerTransfered = isLockerTransfered
    this.lockerTransferedAt = lockerTransferedAt
    this.lockerTransferedReason = lockerTransferedReason
  }

  isOnGoing() {
    return this.status === RentalStatus.STATUS_ONGOING
  }

  isEnded() {
    return this.status === RentalStatus.STATUS_ENDED
  }

  isInOvertime() {
    const now = DateTime.fromObject(
      {},
      { zone: this.endDateIncludingOvertimeGracePeriod.zoneName ?? undefined },
    )

    return now > this.endDateIncludingOvertimeGracePeriod
  }

  isAlmostEnding() {
    const now = DateTime.fromObject(
      {},
      { zone: this.durationPeriod.endDate.zoneName ?? undefined },
    )

    const warningTimeBeforeEnd = this.durationPeriod.endDate.minus({
      minutes: Rental.MINUTES_TO_SHOW_WARNING_BEFORE_END,
    })

    return warningTimeBeforeEnd < now
  }

  isFirstTimeOpeningTheLocker() {
    return this.lockerOpenedHistory.length === 0
  }

  isWithinFirstTimeOpeningGracePeriod(now: DateTime) {
    if (this.isFirstTimeOpeningTheLocker()) {
      return true
    }

    const endDate =
      this.configuration.oneTimeOpen.gracePeriodOnFirstOpen.calculate(
        this.lockerOpenedHistory[0],
      )

    return now.setZone(this.startedDate.zone) < endDate
  }

  wasLockerOpenSinceTransfer() {
    if (!this.isLockerTransfered) return true
    if (!this.lockerTransferedAt) return true

    if (this.isFirstTimeOpeningTheLocker()) return false

    return (
      this.lockerTransferedAt <
      this.lockerOpenedHistory[this.lockerOpenedHistory.length - 1]
    )
  }

  setAuthentications(authentications: Array<Authentication>): void {
    this.authentications = authentications
  }

  static fromJson(data: RentalJson) {
    return new this(
      data.id,
      data.status,
      Unit.fromJson(data.unit),
      Locker.fromJson(data.locker),
      LockerType.fromJson(data.lockerType),
      data.authentications.map((item) => Authentication.fromJson(item)),
      DurationPeriod.fromJson(data.durationPeriod),
      DateTime.fromISO(data.startedDate.date, {
        zone: data.startedDate.timezone,
      }),
      DateTime.fromISO(data.endDateIncludingOvertimeGracePeriod.date, {
        zone: data.endDateIncludingOvertimeGracePeriod.timezone,
      }),
      data.endedDate
        ? DateTime.fromISO(data.endedDate.date, {
            zone: data.endedDate.timezone,
          })
        : null,
      data.rentalEndingScheduledAt
        ? DateTime.fromISO(data.rentalEndingScheduledAt.date, {
            zone: data.rentalEndingScheduledAt.timezone,
          })
        : null,
      RentalConfiguration.fromJson(data.configuration),
      data.lockerOpenedHistory.map((item) =>
        DateTime.fromISO(item.date, {
          zone: item.timezone,
        }),
      ),
      data.isLockerTransfered,
      data.lockerTransferedAt
        ? DateTime.fromISO(data.lockerTransferedAt.date, {
            zone: data.lockerTransferedAt.timezone,
          })
        : null,
      data.lockerTransferedReason,
    )
  }

  toJson(): RentalJson {
    return {
      id: this.id,
      status: this.status,
      unit: this.unit,
      locker: this.locker.toJson(),
      lockerType: this.lockerType.toJson(),
      authentications: this.authentications,
      durationPeriod: this.durationPeriod.toJson(),
      startedDate: {
        date: this.startedDate.toISO() ?? '',
        timezone: this.startedDate.zoneName ?? '',
      },
      endDateIncludingOvertimeGracePeriod: {
        date: this.endDateIncludingOvertimeGracePeriod.toISO() ?? '',
        timezone: this.endDateIncludingOvertimeGracePeriod.zoneName ?? '',
      },
      endedDate: this.endedDate
        ? {
            date: this.endedDate.toISO() ?? '',
            timezone: this.endedDate.zoneName ?? '',
          }
        : null,
      rentalEndingScheduledAt: this.rentalEndingScheduledAt
        ? {
            date: this.rentalEndingScheduledAt.toISO() ?? '',
            timezone: this.rentalEndingScheduledAt.zoneName ?? '',
          }
        : null,
      configuration: this.configuration,
      lockerOpenedHistory: this.lockerOpenedHistory.map((dt) => ({
        date: dt.toISO() ?? '',
        timezone: dt.zoneName ?? '',
      })),
      isLockerTransfered: this.isLockerTransfered,
      lockerTransferedAt: this.lockerTransferedAt
        ? {
            date: this.lockerTransferedAt.toISO() ?? '',
            timezone: this.lockerTransferedAt.zoneName ?? '',
          }
        : null,
      lockerTransferedReason: this.lockerTransferedReason,
    }
  }
}
